import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import {
  Project,
  ProjectConfiguration,
  ProjectMode,
  ProjectSubmitResult,
} from 'src/app/models/project.model';
import {
  CONFIGURATION_SNAPSHOT_VERSION,
  ConfigurationSnapshot,
} from 'src/app/models/configuration-snapshot.model';
import { ProjectService, SubmitProjectPayload } from 'src/app/services/project/project.service';
import { ProductTabsService } from 'src/app/services/product_windows/products-tabs.service';
import { ConfiguratorStateService } from 'src/app/services/configurator-state/configurator-state.service';
import { ConfiguratorResetService } from 'src/app/services/configurator-reset/configurator-reset.service';
import { PDFService } from 'src/app/services/PDF/pdf.service';
import { LineSelectionService } from 'src/app/services/line-selection/line-selection.service';
import { EntrySelectionService } from 'src/app/services/entry-selection/entry-selection.service';
import { normalizeOrderQuantity } from './order-quantity.util';
import { pruneSubsumedProductLimits } from 'src/app/shared/pdf-generator/prune-subsumed';

/**
 * Owns the current project and its cart.
 *
 * Every run goes through here, including "single configuration" — that is just
 * a project with mode 'single' that skips the name/production-code step and
 * goes straight to order details after one configuration. Keeping one code
 * path avoids forking every guard, query and submit branch on a nullable
 * project.
 */
@Injectable({ providedIn: 'root' })
export class ProjectCartService {
  /** Survives a refresh so a half-built project is not stranded. */
  private static readonly ACTIVE_KEY = 'pca_active_project';

  private readonly _project = new BehaviorSubject<Project | null>(null);
  private readonly _configurations = new BehaviorSubject<ProjectConfiguration[]>([]);

  /**
   * Per-configuration order quantity. Not backend-persisted (there is no
   * `quantity` column on project_configurations — it is only ever sent as
   * part of the final submit payload), so this is in-memory for the life of
   * the project, same as it always was; the only change is that it now lives
   * here instead of being duplicated locally in order-details, so a quantity
   * set on the Cart page is the same one order-details submits.
   */
  private readonly _configQuantities = new BehaviorSubject<{ [configurationId: number]: number }>({});

  /** Set by beginEdit(), consumed once by NavbarComponent on restore. */
  private pendingRestore: ConfigurationSnapshot | null = null;
  private _editingConfigurationId: number | null = null;

  /**
   * Room choice for the *next* configuration, captured by the "new product"
   * modal on the cart screen and consumed once by addCurrentConfiguration().
   * null -> the configuration inherits the project's production code and gets
   * no room label (the first configuration, or single mode).
   */
  private _pendingRoom: { productionCode: string | null; roomLabel: string | null } | null = null;

  /**
   * The line the user was browsing before opening a cart item for editing.
   * beginEdit() switches the line to the edited item's, and this puts it back.
   */
  private lineBeforeEdit: string | null = null;

  readonly project$: Observable<Project | null> = this._project.asObservable();
  readonly configurations$: Observable<ProjectConfiguration[]> = this._configurations.asObservable();
  readonly mode$: Observable<ProjectMode | null> = this._project.pipe(map(p => p?.mode ?? null));
  readonly configQuantities$: Observable<{ [configurationId: number]: number }> = this._configQuantities.asObservable();

  constructor(
    private projectService: ProjectService,
    private productWindowService: ProductTabsService,
    private configuratorState: ConfiguratorStateService,
    private reset: ConfiguratorResetService,
    private pdfService: PDFService,
    private lineSelection: LineSelectionService,
    private entrySelection: EntrySelectionService
  ) { }

  /* ---------------------------------------------------------------- *
   * Current project
   * ---------------------------------------------------------------- */

  get project(): Project | null {
    return this._project.getValue();
  }

  get mode(): ProjectMode | null {
    return this._project.getValue()?.mode ?? null;
  }

  get configurations(): ProjectConfiguration[] {
    return this._configurations.getValue();
  }

  get hasActiveProject(): boolean {
    return this._project.getValue() !== null;
  }

  get editingConfigurationId(): number | null {
    return this._editingConfigurationId;
  }

  get configQuantities(): { [configurationId: number]: number } {
    return this._configQuantities.getValue();
  }

  getConfigQuantity(configurationId: number): number {
    return normalizeOrderQuantity(this._configQuantities.getValue()[configurationId]);
  }

  setConfigQuantity(configurationId: number, quantity: unknown): void {
    this._configQuantities.next({
      ...this._configQuantities.getValue(),
      [configurationId]: normalizeOrderQuantity(quantity),
    });
  }

  startSingle(): Observable<Project> {
    return this.createProject('single', null, null);
  }

  startProject(name: string, productionCode: string): Observable<Project> {
    return this.createProject('project', name, productionCode);
  }

  private createProject(mode: ProjectMode, name: string | null, productionCode: string | null): Observable<Project> {
    return this.projectService.create(mode, name, productionCode, this.lineSelection.selectedLine).pipe(
      tap(project => {
        this.setActiveProject(project);
        // A brand new project starts from a clean configurator.
        this.reset.resetForNewFinalProduct();
      })
    );
  }

  resumeProject(projectId: number): Observable<Project> {
    return this.projectService.get(projectId).pipe(
      tap(project => {
        this.setActiveProject(project);
        if (project.line) {
          this.lineSelection.setLine(project.line);
        }
        this.reset.resetForNewFinalProduct();
      })
    );
  }

  /**
   * Re-attach to the project stored before a refresh. Returns null (and clears
   * the pointer) if it no longer exists or belongs to someone else.
   */
  restoreActiveProject(): Observable<Project | null> {
    const stored = localStorage.getItem(ProjectCartService.ACTIVE_KEY);
    if (!stored) { return of(null); }

    const projectId = Number(stored);
    if (!Number.isFinite(projectId) || projectId <= 0) {
      localStorage.removeItem(ProjectCartService.ACTIVE_KEY);
      return of(null);
    }

    return this.projectService.get(projectId).pipe(
      map(project => {
        // A fully submitted project is finished. Don't silently drop the user
        // back into it on a refresh — for a single run that means landing in a
        // "Send again" screen with the previous offer. It stays reachable from
        // /mode ("View") when the project list actually needs it.
        if (project.status === 'submitted') {
          localStorage.removeItem(ProjectCartService.ACTIVE_KEY);
          return null;
        }
        this.setActiveProject(project);
        return project as Project | null;
      }),
      catchError(() => {
        localStorage.removeItem(ProjectCartService.ACTIVE_KEY);
        return of(null);
      })
    );
  }

  /** Leave the project without deleting it; it stays resumable. */
  abandonProject(): void {
    this._project.next(null);
    this._configurations.next([]);
    this._configQuantities.next({});
    this._editingConfigurationId = null;
    this.pendingRestore = null;
    localStorage.removeItem(ProjectCartService.ACTIVE_KEY);
    this.reset.resetForNewFinalProduct();
  }

  private setActiveProject(project: Project): void {
    this._project.next(project);
    this._configurations.next(project.configurations ?? []);
    this._editingConfigurationId = null;
    localStorage.setItem(ProjectCartService.ACTIVE_KEY, String(project.id));
  }

  refreshConfigurations(): Observable<ProjectConfiguration[]> {
    const project = this.project;
    if (!project) { return of([]); }

    return this.projectService.listConfigurations(project.id).pipe(
      tap(configurations => this._configurations.next(configurations))
    );
  }

  /* ---------------------------------------------------------------- *
   * Snapshot
   * ---------------------------------------------------------------- */

  /**
   * Read the whole live configuration in one pass.
   *
   * Called from checkout rather than from the summary panel, for two reasons:
   * the carousel preview image has been written by then, and checkout has
   * already pruned linked-form duplicates out of productLimits — snapshotting
   * before that stores lines that would produce a wrong Soft1 order.
   */
  snapshotCurrent(finalProductId: number): ConfigurationSnapshot {
    const navbar = this.configuratorState.currentNavbarState;

    const localProducts = this.productWindowService.getCurrentLocalProducts();

    return {
      version: CONFIGURATION_SNAPSHOT_VERSION,

      finalProductId,
      line: this.lineSelection.selectedLine,
      entryCategoryId: this.entrySelection.entryCategoryId,

      selected: this.productWindowService.currentSelected,
      materialValues: this.productWindowService.currentMaterialValues,
      // Collapse the less-specific drafts of a recipe (see pruneSubsumedProductLimits):
      // this is the point everything downstream reads from — the PDF, the Soft1
      // ITELINES the backend builds from configuration.productLimits, and the
      // stored cart item.
      productLimits: pruneSubsumedProductLimits(
        this.productWindowService.currentProductLimits,
        localProducts
      ),
      localProducts,
      mpCode: this.productWindowService.currentMPCode,
      additionalFields: this.productWindowService.currentAdditionalFields,
      selectedToolTypes: this.productWindowService.getCurrentSelectedToolTypes(),
      backgroundId: this.productWindowService.currentBackgroundId,
      backgroundColor: this.productWindowService.currentBackgroundColor,

      rawMaterialValues: navbar.materialValues,
      affectedMaterialValues: navbar.affectedMaterialValues,
      materialLimits: navbar.materialLimits,
      aggregatedMaterialValues: navbar.aggregatedMaterialValues,
      selectedToolTypesByCategory: navbar.selectedToolTypesByCategory,
      selectedTabIndex: navbar.selectedTabIndex,

      selectedTemplates: this.pdfService.getCurrentSelectedTemplates(),
      textFieldData: this.pdfService.getCurrentTextFieldData(),
    };
  }

  /* ---------------------------------------------------------------- *
   * Cart operations
   * ---------------------------------------------------------------- */

  addCurrentConfiguration(finalProductId: number, label: string): Observable<ProjectConfiguration> {
    const project = this.project;
    if (!project) {
      throw new Error('addCurrentConfiguration called with no active project.');
    }

    // A single run holds exactly one configuration. Coming back through
    // checkout (e.g. after backing out of order details) must update that one
    // configuration, not stack a second — a multi-item "single" project is
    // exactly the state that makes the app look like it is in project mode.
    if (project.mode === 'single' && this.configurations.length > 0) {
      this._editingConfigurationId = this.configurations[0].id;
      return this.updateEditingConfiguration();
    }

    const snapshot = this.snapshotCurrent(finalProductId);
    const room = this._pendingRoom;
    this._pendingRoom = null;

    return this.projectService.addConfiguration(project.id, {
      final_product_id: finalProductId,
      label: label || `Configuration ${this.configurations.length + 1}`,
      mp_code: snapshot.mpCode?.productCode ?? null,
      mtrl: this.toMtrl(snapshot.mpCode?.MTRL),
      production_code: room?.productionCode ?? project.production_code ?? null,
      // Project-mode items always get a room label. The pending room only lives
      // in memory, so resuming an empty project or refreshing the page would
      // otherwise save the item without one.
      room_label: room?.roomLabel ?? (project.mode === 'project' ? this.suggestedRoomLabel : null),
      configuration: snapshot,
      preview_image: localStorage.getItem('carouselImage'),
    }).pipe(
      tap(created => this._configurations.next([...this.configurations, created]))
    );
  }

  updateEditingConfiguration(): Observable<ProjectConfiguration> {
    const configurationId = this._editingConfigurationId;
    if (configurationId === null) {
      throw new Error('updateEditingConfiguration called while not editing.');
    }

    const existing = this.configurations.find(c => c.id === configurationId);
    const finalProductId = existing?.final_product_id ?? 0;
    const snapshot = this.snapshotCurrent(finalProductId);

    return this.projectService.updateConfiguration(configurationId, {
      final_product_id: finalProductId,
      label: existing?.label,
      mp_code: snapshot.mpCode?.productCode ?? null,
      mtrl: this.toMtrl(snapshot.mpCode?.MTRL),
      configuration: snapshot,
      preview_image: localStorage.getItem('carouselImage'),
    }).pipe(
      tap(updated => {
        this._configurations.next(
          this.configurations.map(c => (c.id === updated.id ? { ...c, ...updated } : c))
        );
        this.endEdit();
      })
    );
  }

  deleteConfiguration(configurationId: number): Observable<void> {
    return this.projectService.deleteConfiguration(configurationId).pipe(
      tap(() => {
        this._configurations.next(this.configurations.filter(c => c.id !== configurationId));

        const { [configurationId]: _removed, ...rest } = this._configQuantities.getValue();
        this._configQuantities.next(rest);

        // Deleting the item currently open for editing leaves the configurator
        // holding state with nowhere to save it.
        if (this._editingConfigurationId === configurationId) {
          this.endEdit();
          this.reset.resetConfiguration();
        }
      })
    );
  }

  reorder(ids: number[]): Observable<ProjectConfiguration[]> {
    const project = this.project;
    if (!project) { return of(this.configurations); }

    return this.projectService.reorder(project.id, ids).pipe(
      tap(configurations => this._configurations.next(configurations))
    );
  }

  /** Clear the configurator so the next final product starts from scratch. */
  startNewConfiguration(): void {
    this.endEdit();
    this.reset.resetForNewFinalProduct();
  }

  /**
   * A sensible default room name for the next configuration — "Room {n}" where
   * n counts from the configurations already in the cart.
   */
  get suggestedRoomLabel(): string {
    return `Room ${this.configurations.length + 1}`;
  }

  /** Room label of the configuration being built or edited, for display. */
  get currentRoomLabel(): string | null {
    if (this._pendingRoom?.roomLabel) { return this._pendingRoom.roomLabel; }
    const editingId = this._editingConfigurationId;
    if (editingId === null) { return null; }
    return this.configurations.find(c => c.id === editingId)?.room_label ?? null;
  }

  /** Room/production-code choice for the next configuration (see _pendingRoom). */
  setPendingRoom(productionCode: string | null, roomLabel: string | null): void {
    const code = productionCode?.trim() || null;
    const label = roomLabel?.trim() || null;
    this._pendingRoom = code || label ? { productionCode: code, roomLabel: label } : null;
  }

  clearPendingRoom(): void {
    this._pendingRoom = null;
  }

  /* ---------------------------------------------------------------- *
   * Editing an existing cart item
   * ---------------------------------------------------------------- */

  /**
   * Load a cart item's snapshot and prepare the configurator to receive it.
   * The snapshot is stashed here rather than pushed straight into the services
   * because the configurator components have not been constructed yet — they
   * pull it via consumePendingRestore() once they are.
   */
  beginEdit(configurationId: number): Observable<ConfigurationSnapshot> {
    return this.projectService.getConfiguration(configurationId).pipe(
      map(configuration => {
        const snapshot = configuration.configuration;

        if (!snapshot || snapshot.version !== CONFIGURATION_SNAPSHOT_VERSION) {
          throw new Error('This configuration was saved by an older version of the app and cannot be edited.');
        }

        this.lineBeforeEdit = this.lineSelection.selectedLine;
        this._editingConfigurationId = configurationId;

        // The preview is read from this single slot by checkout, order details
        // and the PDF window, so point it at the item being edited.
        if (configuration.preview_image) {
          try {
            localStorage.setItem('carouselImage', configuration.preview_image);
          } catch {
            // Quota — the preview is cosmetic, so carry on without it.
            localStorage.removeItem('carouselImage');
          }
        }

        if (snapshot.line) {
          this.lineSelection.setLine(snapshot.line);
        }

        // Clear before navigating, then arm the snapshot — the configurator
        // components pull it in their ngOnInit on the other side of the
        // navigation. Order matters: reset must not wipe what we just armed.
        this.reset.resetForNewFinalProduct();
        this.pendingRestore = snapshot;

        return snapshot;
      })
    );
  }

  /** Read-once: a second navigation must not silently re-restore. */
  consumePendingRestore(): ConfigurationSnapshot | null {
    const snapshot = this.pendingRestore;
    this.pendingRestore = null;
    return snapshot;
  }

  get hasPendingRestore(): boolean {
    return this.pendingRestore !== null;
  }

  private endEdit(): void {
    this._editingConfigurationId = null;
    this.pendingRestore = null;

    // Put the browsing line back so /home is not left filtered by whatever the
    // edited item happened to be.
    if (this.lineBeforeEdit) {
      this.lineSelection.setLine(this.lineBeforeEdit);
    }
    this.lineBeforeEdit = null;
  }

  /* ---------------------------------------------------------------- *
   * Submit
   * ---------------------------------------------------------------- */

  submit(payload: SubmitProjectPayload): Observable<ProjectSubmitResult> {
    const project = this.project;
    if (!project) {
      throw new Error('submit called with no active project.');
    }

    return this.projectService.submit(project.id, payload).pipe(
      tap(result => {
        this._project.next({ ...project, status: result.status });

        // Fold each SALDOC's outcome back onto its cart item so the UI can show
        // per-configuration success, failure and FINCODE.
        this._configurations.next(this.configurations.map(configuration => {
          const outcome = result.results.find(r => r.configuration_id === configuration.id);
          if (!outcome) { return configuration; }

          return {
            ...configuration,
            submit_status: outcome.status,
            soft1_id: outcome.soft1_id,
            fincode: outcome.FINCODE,
            netamnt: outcome.NETAMNT,
            vatamnt: outcome.VATAMNT,
            sumamnt: outcome.SUMAMNT,
            skipped_codes: outcome.skipped_codes,
            submit_error: outcome.error,
          };
        }));
      })
    );
  }

  /** MPCode.MTRL is typed loosely across the app; normalise to a number|null. */
  private toMtrl(value: unknown): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
}
